import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import DataObjectIcon from "@mui/icons-material/DataObject";
import { AI_SNIPPET_VARIABLE_PREFIX } from "../constants";

const STANDARD_MEMBER_FIELDS = [
  { key: "firstName", label: "First Name", icon: <PersonIcon fontSize="small" /> },
  { key: "lastName", label: "Last Name", icon: <PersonIcon fontSize="small" /> },
  { key: "email", label: "Email", icon: <EmailIcon fontSize="small" /> },
  { key: "phone", label: "Phone", icon: <PhoneIcon fontSize="small" /> },
  { key: "linkedinUrl", label: "LinkedIn URL", icon: <LinkedInIcon fontSize="small" /> },
  { key: "companyName", label: "Company Name", icon: <DataObjectIcon fontSize="small" /> },
  { key: "title", label: "Job Title", icon: <DataObjectIcon fontSize="small" /> },
];

interface AISnippetDialogProps {
  open: boolean;
  onClose: () => void;
  onInsert: (snippetPlaceholder: string) => void;
  customVariableFields?: { key: string; label: string }[];
}

const AISnippetDialog: React.FC<AISnippetDialogProps> = ({
  open,
  onClose,
  onInsert,
  customVariableFields = [],
}) => {
  const [prompt, setPrompt] = useState("");
  const [variablesAnchorEl, setVariablesAnchorEl] = useState<null | HTMLElement>(null);
  const [cursorPosition, setCursorPosition] = useState<number | null>(null);
  const textFieldRef = useRef<HTMLInputElement>(null);

  // Get unique custom fields that aren't already in standard fields
  const uniqueCustomFields = customVariableFields.filter(
    (field) => !STANDARD_MEMBER_FIELDS.map((f) => f.key).includes(field.key)
  );

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setPrompt("");
      setCursorPosition(null);
    }
  }, [open]);

  // Handle key press in the text field
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "/") {
      // Prevent the "/" from being typed
      e.preventDefault();
      // Save cursor position
      const input = textFieldRef.current;
      if (input) {
        setCursorPosition(input.selectionStart);
        setVariablesAnchorEl(input);
      }
    }
  };

  // Insert a variable at the cursor position
  const handleInsertVariable = (key: string) => {
    const variable = `{{${key}}}`;

    if (cursorPosition !== null) {
      // Insert at cursor position
      const before = prompt.substring(0, cursorPosition);
      const after = prompt.substring(cursorPosition);
      setPrompt(before + variable + after);
    } else {
      // Append to end
      setPrompt(prompt + variable);
    }

    setVariablesAnchorEl(null);
    setCursorPosition(null);

    // Focus back on the text field
    setTimeout(() => {
      textFieldRef.current?.focus();
    }, 100);
  };

  // Generate the AI snippet placeholder
  const handleInsert = () => {
    if (!prompt.trim()) return;

    // Insert the prompt directly (no encoding needed)
    const placeholder = `{{${AI_SNIPPET_VARIABLE_PREFIX}:${prompt.trim()}}}`;

    onInsert(placeholder);
    onClose();
  };

  // Extract variables from the prompt for preview
  const extractVariables = (text: string): string[] => {
    const pattern = /\{\{([^}]+)\}\}/g;
    const matches = [];
    let match;
    while ((match = pattern.exec(text)) !== null) {
      matches.push(match[1]);
    }
    return matches;
  };

  const usedVariables = extractVariables(prompt);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="h6">Insert AI Snippet</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Write instructions for the AI to generate a personalized snippet at send time.
          Press <strong>/</strong> to insert variables that will be replaced with recipient data.
        </Typography>

        <TextField
          inputRef={textFieldRef}
          autoFocus
          fullWidth
          multiline
          rows={4}
          label="AI Instructions"
          placeholder="e.g., Write a brief compliment about {{companyName}}'s recent growth"
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={handleKeyDown}
          helperText="The AI will generate 1-2 short sentences based on these instructions"
          sx={{ mb: 2 }}
        />

        {usedVariables.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Variables used:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {usedVariables.map((variable, index) => (
                <Chip
                  key={index}
                  label={`{{${variable}}}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
                />
              ))}
            </Box>
          </Box>
        )}

        <Box
          sx={{
            p: 2,
            backgroundColor: "action.hover",
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1, fontWeight: 600 }}>
            Example prompts:
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            • "Mention something relevant about {"{{companyName}}"}'s industry"
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            • "Write a brief compliment for {"{{firstName}}"} based on their role as {"{{title}}"}"
          </Typography>
          <Typography variant="body2">
            • "Reference {"{{companyName}}"}'s focus on {"{{industry}}"}"
          </Typography>
        </Box>

        {/* Variables menu */}
        <Menu
          anchorEl={variablesAnchorEl}
          open={Boolean(variablesAnchorEl)}
          onClose={() => setVariablesAnchorEl(null)}
          PaperProps={{ sx: { maxHeight: 400, width: 280 } }}
        >
          <Typography
            variant="caption"
            sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}
          >
            Member Fields
          </Typography>
          {STANDARD_MEMBER_FIELDS.map((field) => (
            <MenuItem
              key={field.key}
              onClick={() => handleInsertVariable(field.key)}
              dense
            >
              <ListItemIcon>{field.icon}</ListItemIcon>
              <ListItemText
                primary={field.label}
                secondary={`{{${field.key}}}`}
                secondaryTypographyProps={{ variant: "caption", fontFamily: "monospace" }}
              />
            </MenuItem>
          ))}
          {uniqueCustomFields.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography
                variant="caption"
                sx={{ px: 2, py: 0.5, display: "block", color: "text.secondary" }}
              >
                Custom Fields
              </Typography>
              {uniqueCustomFields.map((field) => (
                <MenuItem
                  key={field.key}
                  onClick={() => handleInsertVariable(field.key)}
                  dense
                >
                  <ListItemIcon>
                    <DataObjectIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={field.label}
                    secondary={`{{${field.key}}}`}
                    secondaryTypographyProps={{ variant: "caption", fontFamily: "monospace" }}
                  />
                </MenuItem>
              ))}
            </>
          )}
        </Menu>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleInsert}
          disabled={!prompt.trim()}
        >
          Insert Snippet
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AISnippetDialog;
